import { SizingItemsType } from 'data/types';
import axios from 'utils/api';
import { isEmpty } from 'utils/helpers';

export const SIZING_ITEM_DEFAULTS_API_KEY_MAP: Record<
  string,
  keyof SizingItemsType
> = {
  aboveGradeWalls: 'aboveGradeWall',
  belowGradeWalls: 'belowGradeWall',
  ceilingsBelowRoof: 'ceilingBelowRoofJoists',
  ceilingsOnExposedBeams: 'ceilingOnExposedBeams',
  ceilingsBelowAttic: 'ceilingBelowAttic',
  floorsOverUnconditionedSpace: 'floorOverUnconditionedSpace',
  floorsOverExposedSpace: 'floorOverExposedSpace',
  slabFloors: 'slabFloor',
  basementFloors: 'basementFloor',
  verticalGlass: 'verticalGlass',
  skylights: 'skylight',
  solidDoors: 'solidDoor',
  appliances: 'appliance'
};

export type InsulationDefaultsValues = {
  attic: string;
  ceilingBelowRoofJoists: string;
  ceilingOnExposedBeams: string;
  floorOverBasement: string;
  floorOverGarage: string;
  wallBoard: string;
  wallCavity: string;
  basementFoam: string;
  slabEdge: string;
};

export type RecommendedSizingDefaultsOptions = {
  yearBuilt?: string | number | null;
  climateZone: string;
};

export function transformSizingItemDefaultsFromApi(
  apiData: Record<string, Record<string, unknown>>
): Partial<SizingItemsType> {
  const transformed: Partial<SizingItemsType> = {};

  Object.entries(apiData).forEach(([apiKey, value]) => {
    const frontendKey = SIZING_ITEM_DEFAULTS_API_KEY_MAP[apiKey];
    if (frontendKey && value) {
      Object.assign(transformed, {
        [frontendKey]: value as SizingItemsType[typeof frontendKey]
      });
    }
  });

  return transformed;
}

export function applyInsulationDefaultsToSizingItems(
  defaultSizingItems: SizingItemsType,
  insulationDefaults: InsulationDefaultsValues
): SizingItemsType {
  return {
    ...defaultSizingItems,
    ceilingBelowAttic: {
      ...defaultSizingItems.ceilingBelowAttic,
      insulationValue:
        insulationDefaults.attic as SizingItemsType['ceilingBelowAttic']['insulationValue']
    },
    ceilingBelowRoofJoists: {
      ...defaultSizingItems.ceilingBelowRoofJoists,
      insulationValue:
        insulationDefaults.ceilingBelowRoofJoists as SizingItemsType['ceilingBelowRoofJoists']['insulationValue']
    },
    ceilingOnExposedBeams: {
      ...defaultSizingItems.ceilingOnExposedBeams,
      insulationValue:
        insulationDefaults.ceilingOnExposedBeams as SizingItemsType['ceilingOnExposedBeams']['insulationValue']
    },
    floorOverUnconditionedSpace: {
      ...defaultSizingItems.floorOverUnconditionedSpace,
      insulationValue:
        insulationDefaults.floorOverBasement as SizingItemsType['floorOverUnconditionedSpace']['insulationValue']
    },
    floorOverExposedSpace: {
      ...defaultSizingItems.floorOverExposedSpace,
      insulationValue:
        insulationDefaults.floorOverGarage as SizingItemsType['floorOverExposedSpace']['insulationValue']
    },
    aboveGradeWall: {
      ...defaultSizingItems.aboveGradeWall,
      boardInsulation:
        insulationDefaults.wallBoard as SizingItemsType['aboveGradeWall']['boardInsulation'],
      cavityInsulation:
        insulationDefaults.wallCavity as SizingItemsType['aboveGradeWall']['cavityInsulation']
    },
    belowGradeWall:
      insulationDefaults.basementFoam !== 'None'
        ? {
            ...defaultSizingItems.belowGradeWall,
            insulationValue:
              insulationDefaults.basementFoam as SizingItemsType['belowGradeWall']['insulationValue'],
            insulationType: 'Closed Cell Foam Board' as const,
            insulationCoverage: 'Sill Plate to Floor' as const
          }
        : {
            ...defaultSizingItems.belowGradeWall,
            insulationValue: 'None' as const,
            insulationType: 'None' as const,
            insulationCoverage: 'None' as const
          },
    slabFloor:
      insulationDefaults.slabEdge !== 'None'
        ? {
            ...defaultSizingItems.slabFloor,
            slabInsulationArrangement: 'Vertical Board Insulation' as const,
            insulationValue:
              insulationDefaults.slabEdge as SizingItemsType['slabFloor']['insulationValue']
          }
        : {
            ...defaultSizingItems.slabFloor,
            slabInsulationArrangement: 'None' as const,
            insulationValue: 'None' as const
          }
  };
}

export async function fetchRecommendedSizingDefaults(
  currentDefaults: SizingItemsType,
  options: RecommendedSizingDefaultsOptions
): Promise<SizingItemsType> {
  const response = await axios.get('structure/sizing-item-defaults');
  const transformed = transformSizingItemDefaultsFromApi(
    response.data as Record<string, Record<string, unknown>>
  );
  const merged = {
    ...currentDefaults,
    ...transformed
  };

  if (isEmpty(options.yearBuilt)) {
    return merged;
  }

  const insulationResponse = await axios.get(
    `sizing/insulation-defaults?year=${options.yearBuilt}&climateZone=${options.climateZone}`
  );

  if (!insulationResponse.data) {
    return merged;
  }

  return applyInsulationDefaultsToSizingItems(
    merged,
    insulationResponse.data as InsulationDefaultsValues
  );
}
